/**
 * Which identity provider this build talks to, and how.
 *
 * `VITE_AUTH_PROVIDER` selects the SDK behind `useAuthSession()`:
 *
 * - `workos` (default) — `@workos-inc/authkit-react` (WorkOS AuthKit),
 *   configured by `VITE_WORKOS_CLIENT_ID` and, once Studio has a custom
 *   authentication domain, `VITE_WORKOS_API_HOSTNAME`. AuthKit has no audience
 *   and no build-time organization: the organization comes from the session
 *   itself.
 * - `oidc` — `react-oidc-context` / `oidc-client-ts` against any OIDC issuer
 *   (`VITE_AUTH_ISSUER_URL`, `VITE_AUTH_CLIENT_ID`, optional
 *   `VITE_AUTH_AUDIENCE`). Authorization code + PKCE, refresh tokens on. Local
 *   Keycloak uses this.
 *
 * Each `import.meta.env.*` read below is a static member access on purpose:
 * Vite's `define` only substitutes those exact expressions, so indexing the
 * env object dynamically would lose the per-environment values.
 */

export type AuthProviderKind = 'oidc' | 'workos';

export interface ResolvedAuthConfig {
  provider: AuthProviderKind;
  /** False when the provider's required variables are missing (unguarded local shell). */
  configured: boolean;
  clientId?: string;
  /** OIDC only: full issuer URL, verbatim (may carry a path). */
  issuerUrl?: string;
  /** WorkOS only: custom authentication domain; AuthKit uses WorkOS's own when unset. */
  apiHostname?: string;
  /** OIDC only: API audience requested for access tokens. */
  audience?: string;
  /** OIDC only: scopes requested on sign-in. */
  scope: string;
}

/** `offline_access` is what most issuers (Keycloak, Okta, Entra) require to mint a refresh token. */
export const DEFAULT_OIDC_SCOPE = 'openid profile email offline_access';

/**
 * Build-time literal. Vite's `define` turns `import.meta.env.VITE_AUTH_PROVIDER`
 * into a string constant, so branches on this fold away and only the selected
 * SDK is bundled. `AUTH.provider` is the same answer for runtime code.
 */
export const IS_OIDC_BUILD = import.meta.env.VITE_AUTH_PROVIDER === 'oidc';

type Env = Record<string, string | undefined>;

function nonEmpty(value: string | undefined): string | undefined {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
}

function providerKind(value: string | undefined): AuthProviderKind {
  return value === 'oidc' ? value : 'workos';
}

export function resolveAuthConfig(env: Env): ResolvedAuthConfig {
  const provider = providerKind(env.VITE_AUTH_PROVIDER);
  const scope = nonEmpty(env.VITE_AUTH_SCOPE) ?? DEFAULT_OIDC_SCOPE;

  if (provider === 'oidc') {
    const issuerUrl = nonEmpty(env.VITE_AUTH_ISSUER_URL);
    const clientId = nonEmpty(env.VITE_AUTH_CLIENT_ID);
    return {
      provider,
      configured: Boolean(issuerUrl && clientId),
      clientId,
      issuerUrl,
      audience: nonEmpty(env.VITE_AUTH_AUDIENCE),
      scope,
    };
  }

  const clientId = nonEmpty(env.VITE_WORKOS_CLIENT_ID);
  return {
    provider,
    configured: Boolean(clientId),
    clientId,
    apiHostname: nonEmpty(env.VITE_WORKOS_API_HOSTNAME),
    scope,
  };
}

export const AUTH: ResolvedAuthConfig = resolveAuthConfig({
  VITE_AUTH_PROVIDER: import.meta.env.VITE_AUTH_PROVIDER,
  VITE_AUTH_ISSUER_URL: import.meta.env.VITE_AUTH_ISSUER_URL,
  VITE_AUTH_CLIENT_ID: import.meta.env.VITE_AUTH_CLIENT_ID,
  VITE_AUTH_AUDIENCE: import.meta.env.VITE_AUTH_AUDIENCE,
  VITE_AUTH_SCOPE: import.meta.env.VITE_AUTH_SCOPE,
  VITE_WORKOS_CLIENT_ID: import.meta.env.VITE_WORKOS_CLIENT_ID,
  VITE_WORKOS_API_HOSTNAME: import.meta.env.VITE_WORKOS_API_HOSTNAME,
});

/** True when a provider is fully configured; false renders the unguarded local shell. */
export const AUTH_CONFIGURED = AUTH.configured;
export const AUTH_AUDIENCE = AUTH.audience;
